<template>
  <div>
    <PageHeader
      :page="page"
      :actions="headerActionConfig"
      left-side-class="col-xl-8"
      right-side-class="col-xl-4 d-flex align-items-end justify-content-end pb-2"
    />
    <board class="mb-4" :details="generalInfoBoard" />
    <progress-legend :items="progressLevels" class="mb-3" />
    <Error
      v-if="isPeiDifferent"
      :message="
        'Se identificó un nuevo PEI vigente para el estudiante, por lo que se reinició el informe de progreso para actualizar el programa de servicios.'
      "
      :is-alert-mode="true"
    />
    <educational-services
      class="mb-5"
      :items="progressReportDetail.educationalServices"
      :is-validating="isValidating"
      @display="onDisplayEducationalService"
    >
      <template v-slot:default="{ item }">
        <educational-service-detail
          :detail="item"
          :read-only="readOnly"
          :weeks="listOfWeeks"
          :selected-week="selectedWeek"
          :progress-levels="progressLevels"
          :is-validating="isValidating"
          :worked-objectives.sync="workedObjectives"
          @update:workedObjectives="onObjectiveProgressSelect"
        />
      </template>
    </educational-services>
  </div>
</template>

<script>
import PageHeader from 'PageHeader';
import Board from 'Board';
import Error from '@/components/common/Error.vue';
import peiService from '@/services/pei/peiAcademicProgressCalendar.js';
import {
  ProgressLegend,
  EducationalServices,
  EducationalServiceDetail,
} from './components';
import alertMixin from '@quisit/mipe-utils/mixins/alert.mixin.js';
const EDUCATIONAL_SERVICES_MAIN_PAGE = 'pres-main';
import { pieAcademicProgress } from '@/utils/constants/pei/progress-report-educational-services/pieAcademicProgress';
import transitionStudentService from '@/services/pei/transitionStudent.js';
import documentsService from '@/services/report';

export default {
  name: 'Main',
  components: {
    PageHeader,
    Board,
    ProgressLegend,
    EducationalServices,
    EducationalServiceDetail,
    Error,
  },
  mixins: [alertMixin],
  data: () => ({
    page: {
      icon: 'fas fa-user-edit',
      title: 'Programa de Servicios Educativos ',
      routes: [
        'Inicio',
        'PEI',
        'Informe de Progreso para Servicios Educativos',
        'Programa de Servicios Educativos ',
      ],
    },
    progressReportDetail: {
      generalInfo: null,
      educationalServices: [],
    },
    academicProgressId: null,
    workedObjectives: [],
    progressLevels: [],
    modelToSave: {},
    listOfWeeks: [],
    isValidating: false,
  }),
  computed: {
    headerActionConfig() {
      return [
        {
          text: 'Regresar',
          icon: 'fas fa-arrow-circle-left',
          variant: 'success',
          click: () => this.goBack(),
        },
        {
          text: 'Guardar',
          icon: 'fas fa-save',
          variant: 'outline-success',
          visible: !this.readOnly,
          click: () => this.saveProgressReport(),
        },
        {
          text: 'Someter',
          icon: 'fas fa-flag',
          variant: 'success',
          visible: !this.readOnly,
          click: () => this.submitProgressReports(),
        },
        {
          text: 'Imprimir',
          icon: 'fas fa-print pr-1',
          variant: 'success',
          click: () => this.handlerPrintReport(),
          visible: this.readOnly,
        },
      ];
    },
    studentSieId() {
      return this.$route.params.studentSie;
    },
    weekId() {
      return this.$route.params.week;
    },
    weekLabel() {
      return this.selectedWeek?.fullName;
    },
    academicYearId() {
      return this.$route.params.academicYear;
    },
    isPeiDifferent() {
      return this.progressReportDetail.generalInfo?.peiDifferentFlag;
    },
    isPeiDifferent() {
      return this.progressReportDetail.generalInfo?.peiDifferentFlag;
    },
    status() {
      switch (this.progressReportDetail.generalInfo?.progressReportStatusId) {
        case pieAcademicProgress.Unavailable:
          return {
            color: 'circle-unavailable',
            text: 'No Disponible',
          };
        case pieAcademicProgress.Unset:
        case pieAcademicProgress.Pending:
          return {
            color: 'circle-pending',
            text: 'Pendiente',
          };
        case pieAcademicProgress.Submitted:
          return {
            color: 'circle-submitted',
            text: 'Sometido',
          };
        case pieAcademicProgress.Discussed:
          return {
            color: 'circle-discussed',
            text: 'Discutido',
          };
        case pieAcademicProgress.Amended:
          return {
            color: 'circle-amended',
            text: 'Enmendado',
          };
        case pieAcademicProgress.NotApply:
          return {
            color: 'circle-not-apply',
            text: 'No Aplica (N/A)',
          };
        default:
          return {
            color: 'circle-unavailable',
            text: 'No Disponible',
          };
      }
    },
    readOnly() {
      return (
        Boolean(this.academicProgressId) &&
        (this.progressReportDetail.generalInfo?.progressReportStatusId ==
          pieAcademicProgress.Submitted ||
          this.progressReportDetail.generalInfo?.progressReportStatusId ==
            pieAcademicProgress.Discussed ||
          this.progressReportDetail.generalInfo?.progressReportStatusId ==
            pieAcademicProgress.Amended)
      );
    },
    peiIdFormatted() {
      if (!this.progressReportDetail.generalInfo?.peiId) return;
      const originalString = this.progressReportDetail.generalInfo?.peiId;
      return originalString.slice(0, 4) + '-' + originalString.slice(4);
    },
    generalInfoBoard() {
      const data = this.progressReportDetail.generalInfo;
      return [
        [
          {
            label: 'Año Académico:',
            icon: 'fas fa-calendar-alt',
            data: data?.academicYear,
            cols: 'col-12 col-md-2',
          },
          {
            label: 'Periodo:',
            icon: 'fas fa-calendar-alt',
            data: this.weekLabel,
            cols: 'col-12 col-md-3',
          },
          {
            label: 'Nombre del Estudiante:',
            icon: 'fas fa-address-book',
            data: `${data?.studentName} - ${data?.studentSieId}`,
            cols: 'col-12 col-md-3',
          },
          {
            label: 'Impedimento:',
            icon: 'fas fa-ballot-check',
            data: data?.impediment,
            cols: 'col-12 col-md-3',
          },
          {
            label: 'Número de PEI:',
            icon: 'fas fa-ballot-check',
            data: this.peiIdFormatted,
            cols: 'col-12 col-md-2',
          },
          {
            label: 'Escuela:',
            icon: 'fas fa-map-marker-alt',
            data: `${data?.schoolCode} - ${data?.schoolName}`,
            cols: 'col-12 col-md-3',
          },
          {
            label: 'Estatus:',
            icon: 'fas fa-calendar-day',
            data: this.status?.text,
            cols: 'col-12 col-md-4',
          },
        ],
      ];
    },
    selectedWeek() {
      if (!this.weekId) return null;
      return this.listOfWeeks.find((x) => x.id == this.weekId);
    },
    workedObjectivesId() {
      const objetives = this.workedObjectives.map((x) => x.objectiveId);
      return objetives;
    },
    onlyPsc() {
      const param = this.$route.params.onlyPsc;
      if (param === undefined || param === null) return false;
      if (typeof param === 'boolean') return param;
      if (typeof param === 'string') return param.toLowerCase() === 'true';
      return false;
    },
  },
  async mounted() {
    this.academicProgressId = this.$route.params.id;
    if (!this.academicProgressId && (!this.studentSieId || !this.weekId)) {
      this.$router.push({ name: EDUCATIONAL_SERVICES_MAIN_PAGE });
      return;
    }

    await this.dataInit();
  },
  methods: {
    async getProgressReportDetail() {
      const params = {
        studentSieId: this.studentSieId ?? 0,
        weekId: this.weekId ?? 0,
        academicYearId: this.academicYearId ?? 0,
        id: this.academicProgressId,
        onlyPsc: this.onlyPsc,
      };
      const { data } = await peiService.getProgressReportDetail(params);
      const { generalInfo, educationalServices, workedObjectives } = data;
      this.progressReportDetail.generalInfo = generalInfo;
      this.progressReportDetail.educationalServices = educationalServices.map(
        (x) => {
          x.displayed = false;
          return x;
        }
      );
      this.workedObjectives = workedObjectives;
    },
    async getAcademicProgressLevels() {
      const { data } = await peiService.getAcademicProgressLevels();
      this.progressLevels = data;
    },
    async getWeeks() {
      const { data } = await peiService.getWeeks();
      this.listOfWeeks = data.map((e) => {
        const weeks = {
          'Semana 10': '10S',
          'Semana 20': '20S',
          'Semana 30': '30S',
          'Semana 40': '40S',
          'Semana 50 (AEE)': 'AEE',
        };
        return {
          id: e.id,
          name: weeks[e.name],
          fullName: e.name,
        };
      });
    },
    async getProgressReportGoalsAndObjetivesByArea(service) {
      const results = await Promise.all(
        service.weeklyPeriods.map(w =>
          peiService.getProgressReportGoalsAndObjetivesByArea(w.programServiceAreaId)
        )
      );
      service.subAreas = results.flatMap(r => r.data);
    },
    onDisplayEducationalService(service) {
      service.displayed = !service.displayed;
    },
    prepareModelToSave() {
      this.modelToSave = {
        sieId: Number(this.studentSieId),
        week: Number(this.selectedWeek.id),
        academicYearId: Number(this.academicYearId),
        isSubmitted: false,
        onlyPsc: this.onlyPsc,
        details: this.workedObjectives
          .filter((x) => x.weekId == this.selectedWeek.id)
          .map((x) => ({
            peiProgramServiceObjectivelId: x.objectiveId,
            levelId: x.progressLevelId,
          })),
      };
    },
    onObjectiveProgressSelect(e) {
      const objectiveIds = this.workedObjectives
        .filter((x) => x.weekId == this.weekId)
        .map((x) => x.objectiveId);
      this.prepareModelToSave();
      this.progressReportDetail.educationalServices.forEach((area) => {
        area.objectivesCompleted = area.subAreas
          .flatMap(({ objectives }) => objectives)
          .filter(({ objectiveId }) =>
            objectiveIds.includes(objectiveId)
          ).length;
      });
    },
    async loadServiceAreasObjectives() {
      await Promise.all(
        this.progressReportDetail.educationalServices.map(e =>
          this.getProgressReportGoalsAndObjetivesByArea(e)
        )
      );
    },
    async submitProgressReports() {
      const weeklyObjectives = this.progressReportDetail.educationalServices
        .flatMap(({ subAreas }) => {
          return subAreas.flatMap(({ objectives }) => {
            return objectives.flatMap(({ weekDetail }) => weekDetail);
          });
        })
        .filter(
          (w) => w.availableForWeek == true && w.weekNumber == this.weekId
        );

      const isComplete = weeklyObjectives.every((o) =>
        this.modelToSave.details.some(
          (m) => m.peiProgramServiceObjectivelId == o.objectiveId
        )
      );

      if (isComplete) {
        this.modelToSave.isSubmitted = true;
        const { data } = await peiService.saveProgressReport(this.modelToSave);

        if (data) {
          this.ShowToast(
            'Proceso exitoso.',
            'El informe de progreso se ha sometido correctamente.',
            'success'
          );
        }

        this.academicProgressId = data.id;

        if (data.canCreateSaee12) {
          let isConfirmed = await this.ShowCancelModal(
            '¿Desea proceder con la creación de la SAEE-12?',
            'Encontramos que este estudiante actualmente está en grado 12mo lo que es elegible para documentar la SAEE-12 Certificación de destrezas académicas, funcionales y ocupacionales.',
            'Si',
            'No'
          );

          if (!isConfirmed) {
            this.$router.push({
              name: 'educational-services-program',
              params: {
                id: data,
                studentSie: this.studentSieId,
                academicYear: this.academicYearId,
                week: this.selectedWeek.id,
                onlyPsc: this.onlyPsc,
              },
            });

            await this.dataInit();
            return;
          }

          const {
            data: transitionStudentId,
          } = await transitionStudentService
            .saveStudentSAEE12(this.studentSieId)
            .catch((error) => {
              this.ShowToast('Alerta', error.response.data.message, 'error');
            });

          if (transitionStudentId) {
            this.ShowToast(
              'Alerta',
              'Estudiante agregado con éxito',
              'success'
            );
            this.$router.push(
              `/pei/transition-student/saee-12/${transitionStudentId}`
            );
          }
        } else {
          this.$router.push({
            name: 'educational-services-program',
            params: {
              id: data.id,
              studentSie: this.studentSieId,
              academicYear: this.academicYearId,
              week: this.selectedWeek.id,
              onlyPsc: this.onlyPsc,
            },
          });

          await this.dataInit();
        }
      } else {
        this.ShowToast(
          'Error.',
          'Favor de completar los campos requeridos.',
          'error'
        );

        this.isValidating = true;
      }
    },
    goBack() {
      if (this.onlyPsc) {
        this.$router.push({
          name: 'pres-main-pei',
          params: {
            onlyPsc: true,
            studentSieId: this.studentSieId,
          },
        });
        return;
      }

      this.$router.push({
        name: EDUCATIONAL_SERVICES_MAIN_PAGE,
      });
    },
    async saveProgressReport() {
      const { data } = await peiService.saveProgressReport(this.modelToSave);
      if (data) {
        this.ShowToast(
          'Proceso exitoso.',
          'El informe de progreso se ha guardado correctamente.',
          'success'
        );
        await this.getProgressReportDetail();
      }
    },
    async dataInit() {
      await Promise.all([
        this.getProgressReportDetail(),
        this.getAcademicProgressLevels(),
        this.getWeeks(),
      ]).then(() => {
        this.prepareModelToSave();
      });
    },
    async handlerPrintReport() {
      await documentsService.downloadStudentAcademicProgress(
        this.academicProgressId
      );
    },
  },
};
</script>
<style scoped lang="scss"></style>
